'use client';

import { useState, useCallback } from 'react';
import { VStack } from '@terra/ui-styled-system';
import {
  Button,
  ConfirmationDialog,
  SettingsBooleanControl,
  SettingsRow,
  toasterApi,
  SettingsEmptyState,
  SettingsListSkeleton,
} from '@terra/ui-design-system';
import { CableIcon, Add01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type {
  McpServerResponseDto,
  McpCopilotAvailableServerDto,
} from '@terra/shared-contracts-mcp-servers';
import {
  useMcpServers,
  useDeleteMcpServer,
  useTestMcpServer,
  useUpdateMcpServer,
  useCopilotAvailableMcpServers,
  useOptInToCopilotMcp,
  useOptOutFromCopilotMcp,
} from '../../hooks/api/useMcpServers';
import { McpServerFormDialog } from './McpServerFormDialog';
import { getMcpServerDisplayName } from '@terra/shared-utils';
import { ProxySettingsGroup } from './proxy-settings/ProxySettingsGroup';
import { McpServerRow } from './mcp-servers/McpServerRow';

export function McpServersTab() {
  const t = useTranslations('settings.mcpServerForm');
  const tCommon = useTranslations('common');
  const { servers, isLoading } = useMcpServers();
  const deleteMutation = useDeleteMcpServer();
  const testMutation = useTestMcpServer();
  const updateMutation = useUpdateMcpServer();
  const { servers: tenantServers, isLoading: tenantLoading } =
    useCopilotAvailableMcpServers();
  const optInMutation = useOptInToCopilotMcp();
  const optOutMutation = useOptOutFromCopilotMcp();
  const [showForm, setShowForm] = useState(false);
  const [editingServer, setEditingServer] =
    useState<McpServerResponseDto | null>(null);
  const [serverToDelete, setServerToDelete] =
    useState<McpServerResponseDto | null>(null);
  const handleAdd = useCallback(() => {
    setEditingServer(null);
    setShowForm(true);
  }, []);
  const handleEdit = useCallback((server: McpServerResponseDto) => {
    setEditingServer(server);
    setShowForm(true);
  }, []);
  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingServer(null);
  }, []);
  const confirmDelete = useCallback(async () => {
    if (!serverToDelete) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(serverToDelete.id);
      toasterApi.success({
        title: t('removeSuccess'),
        description: serverToDelete.name,
      });
      setServerToDelete(null);
    } catch (error: unknown) {
      console.error('Failed to remove MCP server:', error);
      toasterApi.error({
        title: t('removeError'),
        description: t('tryAgain'),
      });
    }
  }, [deleteMutation, serverToDelete, t]);
  const handleTest = useCallback(
    async (server: McpServerResponseDto) => {
      try {
        const result = await testMutation.mutateAsync(server.id);

        if (result.success) {
          toasterApi.success({
            title: t('testSuccess'),
            description: t('testSuccessDescription', {
              toolCount: result.tools.length,
              latencyMs: result.latencyMs,
            }),
          });
        } else {
          toasterApi.error({
            title: t('testFailed'),
            description: result.error ?? '',
          });
        }
      } catch (error: unknown) {
        console.error('Failed to test MCP server:', error);
        toasterApi.error({
          title: t('testError'),
          description: t('testErrorDescription'),
        });
      }
    },
    [testMutation, t],
  );
  const handleTenantOptIn = useCallback(
    async (server: McpCopilotAvailableServerDto, checked: boolean) => {
      try {
        if (checked) {
          await optInMutation.mutateAsync(server.id);
          toasterApi.success({
            title: t('copilotOptInSuccess', { name: server.name }),
            description: '',
          });
        } else {
          await optOutMutation.mutateAsync(server.id);
          toasterApi.success({
            title: t('copilotOptOutSuccess', { name: server.name }),
            description: '',
          });
        }
      } catch (error: unknown) {
        console.error('Failed to update co-pilot MCP:', error);
        toasterApi.error({
          title: t('copilotOptError'),
          description: t('tryAgain'),
        });
      }
    },
    [optInMutation, optOutMutation, t],
  );
  const handleToggleEnabled = useCallback(
    async (server: McpServerResponseDto, enabled: boolean) => {
      try {
        await updateMutation.mutateAsync({ id: server.id, data: { enabled } });
      } catch (error: unknown) {
        console.error('Failed to update MCP server:', error);
        toasterApi.error({
          title: t('saveError'),
          description: t('tryAgain'),
        });
      }
    },
    [updateMutation, t],
  );

  if (isLoading) {
    return <SettingsListSkeleton />;
  }

  const isTenantMutating = optInMutation.isPending || optOutMutation.isPending;

  return (
    <VStack gap="6" alignItems="stretch" w="full">
      <ProxySettingsGroup
        title={t('headerTitle')}
        action={
          <Button size="xs" variant="outline" onClick={handleAdd}>
            <Add01Icon size={14} />
            {t('addServer')}
          </Button>
        }
      >
        {servers.length === 0 ? (
          <SettingsEmptyState>{t('emptyTitle')}</SettingsEmptyState>
        ) : (
          servers.map((server) => (
            <McpServerRow
              key={server.id}
              server={server}
              isToggling={updateMutation.isPending}
              isTesting={testMutation.isPending}
              onToggleEnabled={(enabled) =>
                handleToggleEnabled(server, enabled)
              }
              onTest={() => handleTest(server)}
              onEdit={() => handleEdit(server)}
              onDelete={() => setServerToDelete(server)}
            />
          ))
        )}
      </ProxySettingsGroup>

      {tenantLoading || tenantServers.length > 0 ? (
        <ProxySettingsGroup
          title={t('tenantSectionTitle')}
          description={t('tenantSectionSubtitle')}
        >
          {tenantLoading ? (
            <SettingsListSkeleton count={2} itemHeight="56px" />
          ) : (
            tenantServers.map((server) => (
              <SettingsRow
                key={server.id}
                leadingIcon={<CableIcon size={16} />}
                label={getMcpServerDisplayName(server)}
                description={[
                  server.url,
                  ...server.allowedTargetTypes,
                  server.requiresApproval ? t('approvalRequired') : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                control={
                  <SettingsBooleanControl
                    checked={server.optedIn}
                    disabled={isTenantMutating}
                    onCheckedChange={(details) =>
                      handleTenantOptIn(server, details.checked)
                    }
                  />
                }
              />
            ))
          )}
        </ProxySettingsGroup>
      ) : null}

      {showForm ? (
        <McpServerFormDialog
          key={editingServer?.id ?? 'new'}
          server={editingServer}
          onClose={handleCloseForm}
        />
      ) : null}

      <ConfirmationDialog
        isOpen={serverToDelete !== null}
        onOpenChange={({ open }) => {
          if (!open) setServerToDelete(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteMutation.isPending}
        variant="destructive"
        title={t('removeConfirmTitle')}
        description={t('removeConfirmDescription', {
          name: serverToDelete ? getMcpServerDisplayName(serverToDelete) : '',
        })}
        confirmText={t('removeServer')}
        cancelText={tCommon('cancel')}
      />
    </VStack>
  );
}
